import { ComponentType, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { SerializedStyles } from '@emotion/react';

import MainModal from '@/components/MainModal/MainModal';
import { normalizeInviteOptimizationVariantId } from '@/components/Steps/InviteTeamStep/inviteStepOptimization.util';
import { useAppDispatch, useAppSelector, useLoader, useOnboardingTask, useTracking } from '@/hooks';
import { MainLayout } from '@/layouts/MainLayout';
import { FlowModel } from '@/models/flow.model';
import {
  selectOnboardingPageTitle,
  setEeaOrUkCompany,
  setEUMember,
  setInviteOptimizationResolved,
  updateStep,
} from '@/redux/features/onboardingSlice';
import CompanyRequests from '@/requests/company.requests';
import { Company, CompanyTrackingMode } from '@/types/company.type';
import { AllStepsInfo, StepTypes } from '@/types/steps.type';
import { getEuStateFromCompanySettings, resolveEuStateFromCompany } from '@/utils/company-region.util';
import DeviceAndGeoDetector, { clearGeoCacheOnceInDev } from '@/utils/device-detector.utils';
import { LocalStorageUtility } from '@/utils/localstorage.util';
import SentryService from '@/utils/sentry-service.util';

const GEO_INFO_KEY = 'geo-info';

type StoredGeoInfo = { ip: string; countryCode: string };

export function getInitialEuStateFromCompanyOrCache(company: Company | null): {
  isUEMember: boolean;
  isEeaOrUkCompany: boolean;
} | null {
  if (!company) return null;
  clearGeoCacheOnceInDev();
  const fromCompany = getEuStateFromCompanySettings(company);
  if (fromCompany) {
    if (process.env.NODE_ENV === 'development') {
      const source = typeof company.companySettings?.custom?.isUEMember === 'boolean'
        && typeof company.companySettings?.custom?.isEeaOrUkCompany === 'boolean'
        ? 'company'
        : 'splitTestCountry';
      // eslint-disable-next-line no-console
      console.log(`[OnboardingPageWrapper] getInitialEuStateFromCompanyOrCache: from ${ source }`, {
        isUEMember: fromCompany.isUEMember,
        isEeaOrUkCompany: fromCompany.isEeaOrUkCompany,
      });
    }
    return fromCompany;
  }
  const cached = LocalStorageUtility.getItemWithExpiry<StoredGeoInfo>(GEO_INFO_KEY);
  const countryCode = cached?.countryCode && DeviceAndGeoDetector.normalizeCountryCode(cached.countryCode);
  if (countryCode && countryCode !== 'unknown') {
    const isUEMember = DeviceAndGeoDetector.isCountryInEU(countryCode);
    const isEeaOrUkCompany = DeviceAndGeoDetector.isCountryInEEAOrUK(countryCode);
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[OnboardingPageWrapper] getInitialEuStateFromCompanyOrCache: from cache', {
        countryCode,
        isUEMember,
        isEeaOrUkCompany,
      });
    }
    return { isUEMember, isEeaOrUkCompany };
  }
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('[OnboardingPageWrapper] getInitialEuStateFromCompanyOrCache: no company flags, no valid cache', {
      hasCached: !!cached,
      cachedCountryCode: cached?.countryCode,
    });
  }
  return null;
}

type OnboardingPageWrapperProps = {
  flow: FlowModel;
  css?: SerializedStyles;
  getStep: <T extends StepTypes>(stepKey: keyof T | undefined) => AllStepsInfo;
};

export const withOnboardingPageWrapper = <P extends Record<string, unknown>>(
  Component: ComponentType<P>,
  {
    css,
    flow,
    getStep,
  }: OnboardingPageWrapperProps,
) => (
    function OnboardingPageWrapper(props: P) {
      const dispatch = useAppDispatch();
      const { company } = useAppSelector(state => state.auth);
      const stepId = useAppSelector(state => state.onboarding.currentStep.id);
      // Subscribe to isEUMember so we re-render when geo loads and flow steps (e.g. progress bar) update
      useAppSelector(state => state.onboarding.isEUMember);
      const { onAccountCreated } = useOnboardingTask(flow);
      const { trackOnboardingPage } = useTracking(flow);
      const { showLoader, hideLoader } = useLoader();
      const pageTitle = useSelector(selectOnboardingPageTitle);
      const didFirstLoad = useRef(false);
      const syncEuStateRef = useRef<{ isUEMember: boolean; isEeaOrUkCompany: boolean } | null>(null);
      const disableScroll = stepId === 'phoneNumberStep';

      // Set EU/EEA flow from company settings or geo cache before first paint so the correct
      // flow (progress bar and steps) shows immediately instead of after async geo loads.
      if (company && syncEuStateRef.current === null) {
        const initial = getInitialEuStateFromCompanyOrCache(company);
        if (initial) {
          flow.setUEMemberStatus(initial.isUEMember);
          flow.trackingMode = initial.isUEMember ? 'interactive' : 'mixed';
          syncEuStateRef.current = initial;
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('[OnboardingPageWrapper] initial EU state applied (first paint)', {
              isUEMember: initial.isUEMember,
              isEeaOrUkCompany: initial.isEeaOrUkCompany,
              trackingMode: flow.trackingMode,
            });
          }
        } else if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log('[OnboardingPageWrapper] no initial EU state (waiting for onInitPage geo fetch)');
        }
      }

      useLayoutEffect(() => {
        if (syncEuStateRef.current !== null) {
          const { isUEMember, isEeaOrUkCompany } = syncEuStateRef.current;
          dispatch(setEUMember(isUEMember));
          dispatch(setEeaOrUkCompany(isEeaOrUkCompany));
        }
      }, [dispatch]);

      const onInitPage = useCallback(async (company: Company) => {
        // Check for significant country changes (e.g., VPN usage) during onboarding
        // This ensures flags are set correctly even if geo info was cached from a previous visit
        const resolvedEuState = await resolveEuStateFromCompany(company, { checkSignificantChange: true });
        const isUEMember = resolvedEuState?.isUEMember ?? false;
        const isEeaOrUkCompany = resolvedEuState?.isEeaOrUkCompany ?? false;
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log('[OnboardingPageWrapper] onInitPage: region resolved', {
            isUEMember,
            isEeaOrUkCompany,
            trackingMode: isUEMember ? 'interactive' : 'mixed',
          });
        }
        flow.setUEMemberStatus(isUEMember);

        const persistedInviteVariant = normalizeInviteOptimizationVariantId(
          company?.companySettings?.custom?.inviteStepOptimizationVariant,
        );
        if (persistedInviteVariant) {
          dispatch(setInviteOptimizationResolved(persistedInviteVariant));
        }

        const companyStep = getStep(company?.companySettings?.custom?.onboardingStep);

        dispatch(updateStep({ ...companyStep }));
        dispatch(setEUMember(isUEMember));
        dispatch(setEeaOrUkCompany(isEeaOrUkCompany));

        const trackingMode: CompanyTrackingMode = isUEMember ? 'interactive' : 'mixed';
        const tasksMode = trackingMode === 'mixed' ? 'preset' : 'off';
        flow.trackingMode = trackingMode;

        const existingCustomSettings = company?.companySettings?.custom || {};
        try {
          await CompanyRequests.putCompanySettings(company.id, {
            custom: {
              ...existingCustomSettings,
              isUEMember,
              isEeaOrUkCompany,
              showDemoData: true,
            },
            trackingMode,
            tasksMode,
          });
        } catch (error) {
          await SentryService.captureExceptionWithRetryAfter(error, {
            errorContext: 'failed to update EU/UK flags in OnboardingPageWrapper',
            companyId: company.id,
            isUEMember,
            isEeaOrUkCompany,
          });
        }

        await onAccountCreated(companyStep.stepName);
        trackOnboardingPage(companyStep.stepName);
      }, [dispatch, onAccountCreated, trackOnboardingPage]);

      useEffect(() => {
        if (company && !didFirstLoad.current) {
          didFirstLoad.current = true;

          hideLoader();
          onInitPage(company);
        }
      }, [company, onInitPage, hideLoader, showLoader]);

      return (
        <MainLayout title={ pageTitle } css={ css }>
          <MainModal flow={ flow } disableScroll={ disableScroll }>
            <Component { ...props } />
          </MainModal>
        </MainLayout>
      );
    }
  );
